import type { PurchaseOrderDetail } from "@/modules/inventory/domain/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  formatDateForInput,
  parseCalendarDateFromInput,
} from "@/shared/lib/date-time";
import { formatQtyDisplay } from "@/shared/lib/format-number";
import { confirm } from "@/shared/lib/confirm";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/shared/ui/DatePicker";
import { useEffect, useState } from "react";
import { generateReceiveLotNo } from "./poDraftUtils";

export type ReceiveRowState = {
  poLineId: string;
  itemName: string;
  qtyOpen: number;
  unit: string;
  baseUnit: string;
  toBaseFactor: number;
  enabled: boolean;
  qty: number;
  lotNo: string;
  expiryDate: string;
};

type PurchaseOrderReceiveDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  po: PurchaseOrderDetail;
  isPending?: boolean;
  onConfirm: (
    lines: Array<{
      poLineId: string;
      qty: number;
      lotNo: string;
      expiryDate?: string | null;
    }>,
  ) => void;
};

export function PurchaseOrderReceiveDialog({
  open,
  onOpenChange,
  po,
  isPending,
  onConfirm,
}: PurchaseOrderReceiveDialogProps) {
  const [rows, setRows] = useState<ReceiveRowState[]>([]);

  useEffect(() => {
    if (!open) return;
    setRows(
      po.lines
        .filter((l) => l.qtyOpen > 0)
        .map((l) => ({
          poLineId: l.id,
          itemName: l.itemName,
          qtyOpen: l.qtyOpen,
          unit: l.unit,
          baseUnit: l.baseUnit,
          toBaseFactor: l.toBaseFactor,
          enabled: true,
          qty: l.qtyOpen,
          lotNo: generateReceiveLotNo(),
          expiryDate: "",
        })),
    );
  }, [open, po.lines]);

  const updateRow = (poLineId: string, patch: Partial<ReceiveRowState>) => {
    setRows((prev) =>
      prev.map((r) => (r.poLineId === poLineId ? { ...r, ...patch } : r)),
    );
  };

  const allSelected = rows.length > 0 && rows.every((r) => r.enabled);
  const someSelected = rows.some((r) => r.enabled);
  const headerChecked = allSelected
    ? true
    : someSelected
      ? ("indeterminate" as const)
      : false;

  const toggleAllRows = () => {
    const next = !allSelected;
    setRows((prev) => prev.map((r) => ({ ...r, enabled: next })));
  };

  const handleSubmit = async () => {
    const payload = rows
      .filter((r) => r.enabled && r.qty > 0 && r.lotNo.trim())
      .map((r) => ({
        poLineId: r.poLineId,
        qty: r.qty,
        lotNo: r.lotNo.trim(),
        expiryDate: r.expiryDate.trim() ? r.expiryDate.trim() : null,
      }));
    if (payload.length === 0) return;
    const ok = await confirm({
      title: "ຢືນຢັນຮັບເຂົ້າ?",
      description: `ຈະບັນທຶກ ${payload.length} ບັນທັດເຂົ້າ lot ແລະ ອັບເດດສະຖານະ PO`,
      actionText: "ຢືນຢັນຮັບເຂົ້າ",
    });
    if (!ok) return;
    onConfirm(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,52rem)] max-w-[min(100vw-2rem,56rem)] flex-col gap-4 sm:max-w-[min(100vw-2rem,56rem)]">
        <DialogHeader>
          <DialogTitle>ຮັບເຂົ້າຈາກ PO {po.poNo}</DialogTitle>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີປະລິມານຄ້າງຮັບ — PO ນີ້ຮັບຄົບແລ້ວ
          </p>
        ) : (
          <div className="-mx-1 overflow-x-auto px-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[4.5rem] text-center">
                    <div className="flex flex-col items-center gap-1.5 py-0.5">
                      <Checkbox
                        checked={headerChecked}
                        onCheckedChange={toggleAllRows}
                        aria-label="ເລືອກຮັບທຸກແຖວ"
                        className="size-5 border-2 shadow-sm"
                      />
                      <span className="font-semibold text-[11px] text-primary">
                        ຮັບ
                      </span>
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[12rem]">ລາຍການ</TableHead>
                  <TableHead className="w-28">ຄ້າງ</TableHead>
                  <TableHead className="w-28">ຮັບ</TableHead>
                  <TableHead className="min-w-[11rem]">Lot</TableHead>
                  <TableHead className="min-w-[10rem]">ໝົດອາຍຸ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.poLineId}
                    className={cn(!row.enabled && "bg-muted/20")}
                  >
                    <TableCell className="align-middle">
                      <div
                        className={cn(
                          "mx-auto flex w-fit items-center justify-center rounded-lg border-2 p-2.5 transition-colors",
                          row.enabled
                            ? "border-primary bg-primary/10 shadow-sm"
                            : "border-muted-foreground/30 bg-muted/50",
                        )}
                      >
                        <Checkbox
                          checked={row.enabled}
                          onCheckedChange={(v) =>
                            updateRow(row.poLineId, {
                              enabled: v === true,
                            })
                          }
                          aria-label={`ຮັບ ${row.itemName}`}
                          className="size-5 border-2 shadow-sm data-checked:border-primary"
                        />
                      </div>
                    </TableCell>
                    <TableCell
                      className={cn(
                        "align-top font-medium text-sm",
                        !row.enabled && "text-muted-foreground",
                      )}
                    >
                      <button
                        type="button"
                        className="cursor-pointer text-left hover:text-primary hover:underline"
                        onClick={() =>
                          updateRow(row.poLineId, {
                            enabled: !row.enabled,
                          })
                        }
                      >
                        {row.itemName}
                      </button>
                    </TableCell>
                    <TableCell
                      className={cn(
                        "align-top text-sm",
                        !row.enabled && "text-muted-foreground",
                      )}
                    >
                      <div>
                        {formatQtyDisplay(row.qtyOpen)} {row.unit}
                      </div>
                      {row.toBaseFactor !== 1 ? (
                        <div className="text-muted-foreground text-xs">
                          ຮັບ {formatQtyDisplay(row.qty)} {row.unit} ={" "}
                          {formatQtyDisplay(row.qty * row.toBaseFactor)} {row.baseUnit}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        type="number"
                        min={0}
                        max={row.qtyOpen}
                        step="any"
                        disabled={!row.enabled}
                        value={row.qty}
                        onChange={(e) =>
                          updateRow(row.poLineId, {
                            qty: Number(e.target.value) || 0,
                          })
                        }
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        disabled={!row.enabled}
                        value={row.lotNo}
                        onChange={(e) =>
                          updateRow(row.poLineId, { lotNo: e.target.value })
                        }
                        className="h-9 font-mono text-sm"
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <DatePicker
                        mode="single"
                        disabled={!row.enabled}
                        value={
                          row.expiryDate.trim()
                            ? parseCalendarDateFromInput(row.expiryDate)
                            : null
                        }
                        onChange={(d) =>
                          updateRow(row.poLineId, {
                            expiryDate: d ? formatDateForInput(d) : "",
                          })
                        }
                        placeholder="ໝົດອາຍຸ"
                        className="h-9 w-full min-w-[10rem]"
                        captionLayout="dropdown"
                        fromYear={2020}
                        toYear={2035}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isPending}
            disabled={rows.length === 0}
          >
            ຢືນຢັນຮັບເຂົ້າ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
